import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, X, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError as clientApiError } from "@/lib/api";
import { EP } from "@/lib/endpoints";
import { Seo } from "@/lib/useSeo";
import {
  USERNAME_HINT,
  validateUsername,
  normalizeUsernameOnInput,
  finalCleanUsername,
} from "@/lib/username";
import {
  AuthShell,
  PrimaryButton,
  TextField,
  ErrorBox,
  EyeToggle,
} from "@/components/AuthKit";

/**
 * Signup — 1:1 port of iOS `src/app/(auth)/signup.tsx`.
 * Layout: top-bar back → Y hero (44px) → title "Create your account" →
 *   subtitle "Join BrontoY — a new social platform from Bronto Intelligence." →
 *   USERNAME (with debounced availability check + @ prefix + status icon) →
 *   EMAIL → PASSWORD (eye toggle) → CONFIRM PASSWORD (eye toggle) →
 *   error box → primary "Send verification code" → cross-mode "Log in" link.
 *
 * Legal consent (Privacy Policy, EULA, Community Guidelines) is collected
 * on the onboarding basics step per the iOS AGENTS.md note, so this screen
 * has NO consent checkboxes — matching the iOS behaviour exactly.
 */

export default function Signup() {
  const navigate = useNavigate();
  const { requestAccountVerification, apiError } = useAuth();

  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showCpw, setShowCpw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [availability, setAvailability] = useState({
    checking: false,
    available: null,
    error: "",
  });

  // Debounced live username availability — 1:1 with iOS signup.tsx.
  useEffect(() => {
    const u = username.trim();
    if (!u) {
      setAvailability({ checking: false, available: null, error: "" });
      return;
    }
    const localErr = validateUsername(u);
    if (localErr) {
      setAvailability({ checking: false, available: false, error: localErr });
      return;
    }
    setAvailability({ checking: true, available: null, error: "" });
    const t = setTimeout(async () => {
      try {
        const res = await api.get(EP.usernameAvailability(u));
        const d = res.data;
        const available = d?.available === true || d?.data?.available === true;
        const validationError = d?.validation_error || d?.data?.validation_error;
        setAvailability({
          checking: false,
          available: validationError ? false : available,
          error: validationError || (available === false ? "This username is already taken." : ""),
        });
      } catch (e) {
        setAvailability({ checking: false, available: null, error: clientApiError(e) });
      }
    }, 400);
    return () => clearTimeout(t);
  }, [username]);

  const submit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (loading) return;
    setErr("");
    const cleaned = finalCleanUsername(username);
    const uErr = validateUsername(cleaned);
    if (uErr) return setErr(uErr);
    if (availability.available === false) {
      return setErr(availability.error || "Please choose a different username.");
    }
    if (password !== confirm) return setErr("Passwords do not match.");
    if (password.length < 8) return setErr("Password must be at least 8 characters.");

    setLoading(true);
    try {
      await requestAccountVerification({
        email: email.trim(),
        password,
        username: cleaned,
        terms_accepted: true,
        privacy_accepted: true,
      });
      toast.success("Check your email", { description: "We've sent you a 6-digit code." });
      navigate("/verify");
    } catch (e2) {
      setErr(apiError(e2));
    } finally {
      setLoading(false);
    }
  };

  // Right-adornment icon for the username availability state
  const availIcon = availability.checking ? (
    <Loader2 size={18} className="animate-spin text-muted-foreground mr-1" />
  ) : availability.available === true ? (
    <Check size={18} className="text-green-500 mr-1" strokeWidth={3} />
  ) : availability.available === false ? (
    <X size={18} className="text-destructive mr-1" strokeWidth={3} />
  ) : null;

  return (
    <AuthShell
      testId="signup-screen"
      title="Create your account"
      subtitle="Join BrontoY — a new social platform from Bronto Intelligence."
    >
      <Seo
        title="Sign up for BrontoY"
        description="Create your BrontoY account and join a new social platform from Bronto Intelligence to share moments and start real conversations."
        canonicalPath="/signup"
      />
      <form onSubmit={submit} className="flex flex-col gap-4 mt-8">
        {/* Username with @ prefix + status icon */}
        <div className="flex flex-col gap-1.5">
          <label
            className="text-muted-foreground font-semibold"
            style={{ fontSize: 13, letterSpacing: "0.02em" }}
            htmlFor="signup-username"
          >
            USERNAME
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground text-[16px] pointer-events-none z-10">
              @
            </span>
            <input
              id="signup-username"
              type="text"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              maxLength={30}
              placeholder="yourname"
              value={username}
              onChange={(e) => setUsername(normalizeUsernameOnInput(e.target.value))}
              onBlur={(e) => setUsername(finalCleanUsername(e.target.value))}
              className="w-full h-[52px] bg-secondary rounded-[14px] pl-10 pr-11 text-[16px] text-foreground placeholder:text-muted-foreground border border-border focus:outline-none focus:border-foreground/25 transition-colors"
              data-testid="signup-username-input"
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2">{availIcon}</div>
          </div>
          <p
            className={`text-xs mt-0.5 ${availability.error ? "text-destructive" : "text-muted-foreground"}`}
            data-testid={availability.error ? "signup-username-error" : "signup-username-hint"}
          >
            {availability.error || USERNAME_HINT}
          </p>
        </div>

        <TextField
          id="signup-email"
          label="EMAIL"
          type="email"
          autoComplete="email"
          autoCapitalize="none"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          testId="signup-email-input"
        />

        <TextField
          id="signup-password"
          label="PASSWORD"
          type={showPw ? "text" : "password"}
          autoComplete="new-password"
          autoCapitalize="none"
          placeholder="At least 8 characters"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          rightAdornment={<EyeToggle show={showPw} onToggle={() => setShowPw((s) => !s)} testId="signup-toggle-pw" />}
          testId="signup-password-input"
        />

        <TextField
          id="signup-confirm"
          label="CONFIRM PASSWORD"
          type={showCpw ? "text" : "password"}
          autoComplete="new-password"
          autoCapitalize="none"
          placeholder="Re-enter password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          rightAdornment={<EyeToggle show={showCpw} onToggle={() => setShowCpw((s) => !s)} testId="signup-toggle-cpw" />}
          testId="signup-confirm-password-input"
        />

        {err ? <ErrorBox testId="signup-error">{err}</ErrorBox> : null}

        <PrimaryButton
          type="submit"
          label="Send verification code"
          loading={loading}
          disabled={availability.available === false}
          className="mt-1"
          testId="signup-submit-btn"
        />
      </form>

      <div className="flex items-center justify-center mt-7 text-[14px]">
        <span className="text-muted-foreground">Already have an account?&nbsp;</span>
        <button
          type="button"
          onClick={() => navigate("/login", { replace: true })}
          className="text-foreground font-bold hover:opacity-80 transition-opacity"
          data-testid="signup-to-login"
        >
          Log in
        </button>
      </div>
    </AuthShell>
  );
}
